import { useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import classes from "../homePageNew.module.css";
import SectionEmptyState from "./SectionEmptyState";
import { CategoryIcon } from "./icons";

const BRAND_COLORS = ["#2e6aa8", "#b9770e", "#7a5bb0", "#c0392b", "#3e9a5c", "#e8546b", "#4a90d9", "#8b5cf6"];
const MAIN_BRAND_COLOR = "#0fbe93";

function formatDateLabel(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const Y_TICKS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

// X tick: month-day on the first line, the launched model's standard
// OVERVIEW_LLM_LABELS name beneath it.
function makeXTick(points) {
  return function XTick({ x, y, payload }) {
    const point = points[payload.index];
    return (
      <g transform={`translate(${x},${y})`}>
        <text textAnchor="middle" fill="#666" fontSize={11} dy={12}>
          {point?.dateLabel}
        </text>
        <text textAnchor="middle" fill="#999" fontSize={10} dy={25}>
          {point?.llmName}
        </text>
      </g>
    );
  };
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;
  const sorted = [...payload].sort((a, b) => b.value - a.value);
  return (
    <div className={classes.monChartTooltip}>
      <div className={classes.monChartTooltipDate}>
        {/* Same month-day format the x-axis itself already shows for this
            point — was showing the raw ISO runDate (with the time
            component) here instead. */}
        {row?.dateLabel}
        {row?.llmName && <span style={{ opacity: 0.7, fontWeight: 400 }}> · {row.llmName}</span>}
      </div>
      {sorted.map((entry) => {
        const detail = row?.__scores?.[entry.dataKey];
        return (
          // Name and values stack on their own lines rather than sharing
          // one flex row — a long brand name (e.g. a full legal name with
          // a parenthetical) used to force-keep its own width and push the
          // score values right out past the tooltip's edge instead of
          // wrapping.
          <div key={entry.dataKey} className={classes.monChartTooltipRow}>
            <div className={classes.monChartTooltipNameRow}>
              <span className={classes.monChartTooltipDot} style={{ background: entry.color }} />
              <span className={classes.monChartTooltipName}>{entry.dataKey}</span>
            </div>
            <div className={classes.monChartTooltipVals}>
              Index {detail?.indexScore ?? "—"} · Vis {detail?.visibilityScore ?? "—"} · Sent {detail?.sentimentScore ?? "—"}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Brand-vs-competitors line chart for ONE selected provider (OpenAI,
 * Google, ...), across every run available for it — the same chart the
 * real Monitoring dashboard shows (MONITORING_HOMEPAGE_CHART_CHANGE_SPEC.md).
 * Reads the already-derived `series` off the matching entry in
 * `providerLlms` (see monitoringDerive.js) rather than re-deriving from
 * raw runs itself, so a model-version upgrade mid-history (gpt-5.2 ->
 * gpt-5.4) still draws one continuous line instead of two short ones.
 */
export default function MonitoringChart({ providerLlms, mainBrandName, chartProvider }) {
  const [hidden, setHidden] = useState(() => new Set());

  const activeProvider = providerLlms.find((p) => p.provider === chartProvider) || providerLlms[0];

  const { points, brands } = useMemo(() => {
    if (!activeProvider) return { points: [], brands: [] };
    const brandSet = new Set();
    const rows = activeProvider.series.map((point) => {
      const scores = {};
      const flat = {};
      point.brands.forEach((b) => {
        brandSet.add(b.name);
        scores[b.name] = { indexScore: b.indexScore, visibilityScore: b.visibilityScore, sentimentScore: b.sentimentScore };
        flat[b.name] = b.indexScore;
      });
      return { runDate: point.runDate, dateLabel: formatDateLabel(point.runDate), llmName: point.llmName, __scores: scores, ...flat };
    });
    return { points: rows, brands: [...brandSet] };
  }, [activeProvider]);

  const orderedBrands = useMemo(() => {
    const rest = brands.filter((b) => b !== mainBrandName).sort();
    return mainBrandName && brands.includes(mainBrandName) ? [mainBrandName, ...rest] : rest;
  }, [brands, mainBrandName]);

  const xTick = useMemo(() => makeXTick(points), [points]);

  const toggleLine = (brand) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(brand)) next.delete(brand);
      else next.add(brand);
      return next;
    });
  };

  return (
    <div className={classes.monChartCard}>
      <div className={classes.monChartToolbar}>
        <div className={classes.monChartTitleRow}>
          <span className={classes.monChartTitle}>{mainBrandName} — Index Score by LLM, across runs</span>
        </div>
        <div className={classes.monChartLegend}>
          {orderedBrands.map((brand, i) => {
            const color = brand === mainBrandName ? MAIN_BRAND_COLOR : BRAND_COLORS[i % BRAND_COLORS.length];
            const isHidden = hidden.has(brand);
            return (
              <span
                key={brand}
                className={`${classes.monChartLegendItem} ${isHidden ? classes.monChartLegendItemOff : ""}`}
                onClick={() => toggleLine(brand)}
              >
                <span className={classes.monChartLegendDot} style={{ background: color }} />
                {brand}
              </span>
            );
          })}
        </div>
      </div>

      {points.length === 0 ? (
        <SectionEmptyState icon={CategoryIcon} title="No run history for this LLM" description="Pick a different LLM above to see its trend." />
      ) : (
        <div className={classes.monChartCanvas}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 8, right: 12, left: 0, bottom: 14 }}>
              <CartesianGrid vertical={false} stroke="var(--hpn-border)" />
              <XAxis dataKey="runDate" tick={xTick} interval={0} height={44} axisLine={{ stroke: "var(--hpn-border)" }} tickLine={false} />
              <YAxis
                domain={[0, 100]}
                ticks={Y_TICKS}
                tick={{ fontSize: 10 }}
                axisLine={{ stroke: "var(--hpn-border)" }}
                tickLine={false}
                width={44}
                label={{ value: "Brand Index Score", angle: -90, position: "insideLeft", offset: 4, style: { fontSize: 11, fill: "#666", textAnchor: "middle" } }}
              />
              <Tooltip content={<CustomTooltip />} />
              {orderedBrands.map((brand, i) => {
                const color = brand === mainBrandName ? MAIN_BRAND_COLOR : BRAND_COLORS[i % BRAND_COLORS.length];
                return (
                  <Line
                    key={brand}
                    type="monotone"
                    dataKey={brand}
                    stroke={color}
                    strokeWidth={brand === mainBrandName ? 2.6 : 1.8}
                    dot={{ r: 3, fill: color }}
                    hide={hidden.has(brand)}
                    connectNulls
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
